import React, { useEffect, useState, useRef } from 'react';
import { getDatabase, ref, get, push, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import './Layouts.css';

// Lokale Page Preview Komponente für Layout Templates
const LocalPagePreview = ({ pageData }) => {
    // Importiere die Widget-Komponenten (alle verbleibenden Widgets)
    const ProductInfoWidget = React.lazy(() => import('../WidgetTypes/ProductInfoWidget'));
    const HeroHeader = React.lazy(() => import('../WidgetTypes/HeroHeader'));
    const HiWidget = React.lazy(() => import('../WidgetTypes/HiWidget'));
    const VideoTeaser = React.lazy(() => import('../WidgetTypes/VideoTeaser'));
    const SimplisticHeader = React.lazy(() => import('../WidgetTypes/SimplisticHeader'));
    const SupremeTestimonial = React.lazy(() => import('../WidgetTypes/SupremeTestimonial'));
    const SimplisticQuote = React.lazy(() => import('../WidgetTypes/SimplisticQuote'));
    const MethodologyWidget = React.lazy(() => import('../WidgetTypes/MethodologyWidget'));
    const PersonalBrandingWidget = React.lazy(() => import('../WidgetTypes/PersonalBrandingWidget'));
    const SimplisticTestimonial = React.lazy(() => import('../WidgetTypes/SimplisticTestimonial'));
    const EventInfoWidget = React.lazy(() => import('../WidgetTypes/EventInfoWidget'));
    const CourseInfoWidget = React.lazy(() => import('../WidgetTypes/CourseInfoWidget'));
    const InfiniteBanner = React.lazy(() => import('../WidgetTypes/InfiniteBanner'));
    const QAWidget = React.lazy(() => import('../WidgetTypes/QAWidget'));
    const HolisticTestimonial = React.lazy(() => import('../WidgetTypes/HolisticTestimonial'));
    const SpiritualTestimonial = React.lazy(() => import('../WidgetTypes/SpiritualTestimonial'));
    const SimplisticServices = React.lazy(() => import('../WidgetTypes/SimplisticServices'));
    const BulletPointsWidget = React.lazy(() => import('../WidgetTypes/BulletPointsWidget'));
    const ComparisonWidget = React.lazy(() => import('../WidgetTypes/ComparisonWidget'));
    const BragBar = React.lazy(() => import('../WidgetTypes/BragBar'));
    const SpiritualFeatureBanner = React.lazy(() => import('../WidgetTypes/SpiritualFeatureBanner'));
    const AchievementsBanner = React.lazy(() => import('../WidgetTypes/AchievementsBanner'));
    const SpiritualQuoteCard = React.lazy(() => import('../WidgetTypes/SpiritualQuoteCard'));
    const FancyFooter = React.lazy(() => import('../WidgetTypes/FancyFooter'));

    const renderWidget = (widget) => {
        if (!widget || !widget.type) {
            console.log('⚠️ LayoutTemplates: Invalid widget:', widget);
            return null;
        }
        
        const props = { data: widget, isEditMode: false };
        
        console.log('🔍 LayoutTemplates: Rendering widget type:', widget.type);
        
        switch (widget.type) {
            case 'ProductInfoWidget': return <ProductInfoWidget {...props} />;
            case 'HeroHeader': return <HeroHeader {...props} />;
            case 'HiWidget': return <HiWidget {...props} />;
            case 'VideoTeaser': return <VideoTeaser {...props} />;
            case 'SimplisticHeader': return <SimplisticHeader {...props} />;
            case 'SupremeTestimonial': return <SupremeTestimonial {...props} />;
            case 'SimplisticQuote': return <SimplisticQuote {...props} />;
            case 'MethodologyWidget': return <MethodologyWidget {...props} />;
            case 'PersonalBrandingWidget': return <PersonalBrandingWidget {...props} />;
            case 'SimplisticTestimonial': return <SimplisticTestimonial {...props} />;
            case 'EventInfoWidget': return <EventInfoWidget {...props} />;
            case 'CourseInfoWidget': return <CourseInfoWidget {...props} />;
            case 'QAWidget': return <QAWidget {...props} />;
            case 'HolisticTestimonial': return <HolisticTestimonial {...props} />;
            case 'SpiritualTestimonial': return <SpiritualTestimonial {...props} />;
            case 'SimplisticServices': return <SimplisticServices {...props} />;
            case 'BulletPointsWidget': return <BulletPointsWidget {...props} />;
            case 'ComparisonWidget': return <ComparisonWidget {...props} />;
            case 'BragBar': return <BragBar {...props} />;
            case 'SpiritualFeatureBanner': return <SpiritualFeatureBanner {...props} />;
            case 'AchievementsBanner': return <AchievementsBanner {...props} />;
            case 'SpiritualQuoteCard': return <SpiritualQuoteCard {...props} />;
            case 'FancyFooter': return <FancyFooter {...props} />;
            default: 
                console.log('⚠️ LayoutTemplates: Unknown widget type:', widget.type);
                return (
                    <div style={{ padding: '10px', background: '#f0f0f0', borderRadius: '8px', margin: '5px' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            Unknown widget: {widget.type}
                        </p>
                    </div>
                );
        }
    };

    const pageBackgroundColor = pageData.backgroundColor || '#ffffff';
    
    // Debug: Log page data structure
    console.log('🔍 LayoutTemplates: Page data:', pageData);
    console.log('🔍 LayoutTemplates: Widgets structure:', pageData.widgets);
    
    // Handle both Array and Object widget structures
    let sortedWidgets = [];
    if (pageData.widgets) {
        if (Array.isArray(pageData.widgets)) {
            // Widgets as Array
            sortedWidgets = pageData.widgets.sort((a, b) => (a.position || 0) - (b.position || 0));
            console.log('🔍 LayoutTemplates: Using Array widgets:', sortedWidgets.length);
        } else {
            // Widgets as Object
            sortedWidgets = Object.values(pageData.widgets).sort((a, b) => (a.position || 0) - (b.position || 0));
            console.log('🔍 LayoutTemplates: Using Object widgets:', sortedWidgets.length);
        }
    }
    
    console.log('🔍 LayoutTemplates: Final sorted widgets:', sortedWidgets);

    return (
        <div className="local-page-preview" style={{ 
            width: '100%', 
            height: '690px', 
            overflow: 'auto',
            backgroundColor: pageBackgroundColor,
            borderRadius: '25px',
            border: '1px solid white',
            boxShadow: 'rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px'
        }}>
            <React.Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading Template...</div>}>
                {sortedWidgets.map((widget, index) => (
                    <div key={index} className="widget-boss-wrapper" style={{ backgroundColor: pageBackgroundColor }}>
                        {renderWidget(widget)}
                    </div>
                ))}
                {sortedWidgets.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                        <p>Template Preview</p>
                    </div>
                )}
            </React.Suspense>
        </div>
    );
};

const LayoutTemplates = ({ showLayoutModal, setShowLayoutModal, selectedPageId, setSelectedPageId, selectedUserId, setSelectedUserId }) => {
  const [pages, setPages] = useState([]);
  const [pageName, setPageName] = useState('');
  const [pageDescription, setPageDescription] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const modalRef = useRef();
  const auth = getAuth();
  const db = getDatabase();
  const currentUser = auth.currentUser;
  const navigate = useNavigate();
  const [newPageId, setNewPageId] = useState(null);

  useEffect(() => {
    const pagesRef = ref(db, '/pages');
    // Close modal on outside click
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const pagesRef = ref(db, '/pages');

    get(pagesRef).then((snapshot) => {
        if (snapshot.exists()) {
            const pagesData = snapshot.val();
            // Lade vollständige Page-Daten für lokale Vorschau
            const loadedPages = Object.entries(pagesData)
                .filter(([_, pageInfo]) => pageInfo.isDemo === true) // Filter nach isDemo === true
                .map(([pageId, pageInfo]) => ({
                    // Vollständige Page-Daten für lokale Vorschau
                    ...pageInfo,
                    id: pageId,
                    name: pageInfo.name || 'No Name',
                    customDomain: pageInfo.customDomain || 'No Domain',
                    description: pageInfo.description || 'No Description',
                    // Behalte URL für Fallback
                    url: `to/${pageInfo.customRoute}`,
                }));

            setPages(loadedPages);
        } else {
            console.log("No pages found.");
        }
    }).catch(error => {
        console.error("Error loading pages:", error);
    });
}, []);


  const validateCustomDomain = () => {
    if (!pageName.trim() || !pageDescription.trim() || !customDomain.trim()) {
      alert('Please fill out all fields.');
      return false;
    }
    return true;
  };

  const checkCustomDomainExists = async () => {
    const customDomainRef = ref(db, `/publicPages/${customDomain}`);
    const snapshot = await get(customDomainRef);
    return snapshot.exists();
  };

  const duplicatePageToUserAccount = async () => {
    if (!currentUser) {
        alert('Please log in to use this feature.');
        return;
    }
    if (!validateCustomDomain()) return;

    const exists = await checkCustomDomainExists();
    if (exists) {
        alert('This custom domain is already taken. Please choose another.');
        return;
    }

    // Überprüfen, ob die Seite existiert
    const pageRef = ref(db, `/pages/${selectedPageId}`); // Angepasster Pfad
    const pageSnapshot = await get(pageRef);

    if (!pageSnapshot.exists()) {
        alert('Page not found.');
        return;
    }

    // Daten der Seite abrufen
    const pageData = pageSnapshot.val();

    // Neue Seite für den aktuellen Benutzer erstellen
    const newPageRef = push(ref(db, `/pages`));
    const newPageId = newPageRef.key;

    await set(newPageRef, {
        ...pageData,
        name: pageName,
        description: pageDescription,
        customDomain: customDomain,
        customRoute: customDomain,
        isActive: false,
        id: newPageId,
        userId: currentUser.uid, // Benutzer-ID des aktuellen Nutzers hinzufügen
    });

    // Öffentlich zugängliche Seite speichern, falls ein customDomain gesetzt ist
    if (customDomain) {
        await set(ref(db, `/publicPages/${customDomain}`), { userId: currentUser.uid, pageId: newPageId });
    }

    setIsSuccess(true); // Erfolgsmeldung setzen
    setNewPageId(newPageRef.key); // ID der neuen Seite speichern
};


  const openModal = (pageId, userId) => {
    setSelectedPageId(pageId);
    setSelectedUserId(userId);
    setShowLayoutModal(true);
  };

  const closeModal = () => {
    setShowLayoutModal(false);
    setPageName('');
    setPageDescription('');
    setCustomDomain('');
    setIsSuccess(false);
  };

  return (
    <div>
      <div className="layout-component">
        <div className="layouts">
          {pages.map((page, index) => (
            <div key={index} className="framey-component">
              <div className="framey-buttons">
                <div>
                  <p>{page.name}</p>
                </div>
                <div className="framey-hidden">
                  <button className="framey_button" id="left" onClick={() => openModal(page.id, page.userId)}>
                    <p>Use Template</p>
                    <img src="./img/plus.svg" alt="" />
                  </button>
                </div>
              </div>
              <div className="framey" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LocalPagePreview pageData={page} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {showLayoutModal && (
        <div className="modal-background">
          <div ref={modalRef}>
            {isSuccess ? (
              <div className="modal-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <h2>Yay!</h2>
                <p>You just imported this page.</p>
                <div className="qr-buttons">
                  <button className="qr-modal-open" onClick={() => navigate(`/settings/page/${newPageId}`)}>Edit Page</button>
                  <button className="qr-modal-close" onClick={() => navigate(`/`)}>My Pages</button>
                </div>
              </div>
            ) : (
              <div className="modal-content">
                <h2>Use this Template</h2>
                <label className="modal-content-label">Page Link</label>
                <div className="custom-domain-input" style={{ width: '380px', border: '1px solid lightgrey' }}>
                  <span className="static-text">bonusclub.link/to/</span>
                  <input
                    style={{ border: 'none', fontSize: '15px', marginBottom: '10px', marginLeft: '-2px' }}
                    type="text"
                    name="customDomain"
                    className="pseudo_input"
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value)}
                    placeholder="my_business"
                  />
                </div>
                <label className="modal-content-label">Name of your Page</label>
                <input
                  className="text-input-field-2"
                  type="text"
                  value={pageName}
                  onChange={(e) => setPageName(e.target.value)}
                  placeholder="Page Name"
                />
                <label className="modal-content-label">Describe your Page</label>
                <textarea
                  className="description-input-field-2"
                  value={pageDescription}
                  onChange={(e) => setPageDescription(e.target.value)}
                  placeholder="Short description of your page"
                />
                <div className="qr-buttons">
                  <button className="qr-modal-open" onClick={duplicatePageToUserAccount}>Use Template</button>
                  <button className="qr-modal-close" onClick={closeModal}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

LayoutTemplates.propTypes = {
  showLayoutModal: PropTypes.bool.isRequired,
  setShowLayoutModal: PropTypes.func.isRequired,
  selectedPageId: PropTypes.string.isRequired,
  setSelectedPageId: PropTypes.func.isRequired,
  selectedUserId: PropTypes.string.isRequired,
  setSelectedUserId: PropTypes.func.isRequired,
};

export default LayoutTemplates;
